import { useEffect } from 'react';
import { useMotionPreferences } from './preferences';

export function scrollBehaviorFor(reducedMotion: boolean): ScrollBehavior {
  return reducedMotion ? 'auto' : 'smooth';
}

/**
 * One event-fed RAF owner for report-wide scroll progress, pointer drift, and
 * short ambient movement. It sleeps after the interaction settles.
 */
export function useSharedScrollRuntime(enabled: boolean) {
  const { reducedMotion } = useMotionPreferences();

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const root = document.documentElement;
    let frame = 0;
    let dirty = true;
    let pointerX = 0;
    let pointerY = 0;
    let targetX = 0;
    let targetY = 0;
    let ambientUntil = reducedMotion ? 0 : performance.now() + 4200;

    const requestTick = () => {
      if (!frame && !document.hidden) frame = requestAnimationFrame(tick);
    };
    const tick = (now: number) => {
      frame = 0;
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const progress = Math.min(1, Math.max(0, window.scrollY / maxScroll));
      pointerX += (targetX - pointerX) * 0.1;
      pointerY += (targetY - pointerY) * 0.1;
      const ambientActive = !reducedMotion && now < ambientUntil;
      const ambientX = ambientActive ? Math.sin(now / 1300) * 2.4 : 0;
      const ambientY = ambientActive ? Math.cos(now / 1650) * 1.8 : 0;

      root.style.setProperty('--report-scroll-progress', progress.toFixed(5));
      root.style.setProperty('--report-drift-x', `${(pointerX * 7 + ambientX).toFixed(2)}px`);
      root.style.setProperty('--report-drift-y', `${(pointerY * 5 + ambientY).toFixed(2)}px`);
      dirty = false;

      const pointerMoving = Math.abs(targetX - pointerX) > 0.01 || Math.abs(targetY - pointerY) > 0.01;
      if (dirty || pointerMoving || ambientActive) requestTick();
    };
    const wake = (duration = 1400) => {
      dirty = true;
      if (!reducedMotion) ambientUntil = Math.max(ambientUntil, performance.now() + duration);
      requestTick();
    };
    const onScroll = () => wake(900);
    const onResize = () => wake(1200);
    const onPointerMove = (event: PointerEvent) => {
      targetX = event.clientX / Math.max(1, window.innerWidth) * 2 - 1;
      targetY = event.clientY / Math.max(1, window.innerHeight) * 2 - 1;
      wake(1600);
    };
    const onPointerLeave = () => {
      targetX = 0;
      targetY = 0;
      wake(900);
    };
    const onVisibility = () => {
      if (document.hidden) {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
      } else {
        wake(900);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', onVisibility);
    requestTick();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      root.style.removeProperty('--report-scroll-progress');
      root.style.removeProperty('--report-drift-x');
      root.style.removeProperty('--report-drift-y');
    };
  }, [enabled, reducedMotion]);
}
